import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import { styled as styling } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Color from "../../../assets/colors";

const shouldForwardProp = (prop) => prop !== "levelsCount";

// Dialog Container
export const DialogContainer = styling(Dialog)(({ theme }) => ({
  "&&": {
    ".MuiDialog-container .MuiPaper-root": {
      borderRadius: "24px",
      padding: 0,
      display: "flex",
      flexDirection: "column",
      width: "calc(100% - 64px)",
      maxWidth: "920px",
      height: "70%",
      maxHeight: "750px",
      boxSizing: "border-box",
      overflowX: "hidden",
    },
    ".MuiDialogContent-root": {
      flex: "1 1 auto",
      minHeight: 0,
    },
    "& .MuiBackdrop-root": {
      backdropFilter: "blur(3px)",
      backgroundColor: "#7a7a7a66",
    },
    ".MuiDialog-paper": {
      border: "1.5px solid black",
    },
    " .MuiDialogTitle-root": {
      fontWeight: 700,
    },
  },

  "@media (max-width: 1279px)": {
    "&&": {
      ".MuiDialog-container .MuiPaper-root": {
        width: "calc(100% - 64px)",
        maxWidth: "none",
        height: "70%",
        maxHeight: "720px",
        margin: "32px",
        overflow: "hidden",
      },
      ".MuiDialogContent-root": {
        padding: "20px 24px",
      },
      ".MuiDialogActions-root": {
        padding: "12px 24px",
      },
    },
  },

  "@media (max-width: 640px)": {
    "&&": {
      ".MuiDialog-container .MuiPaper-root": {
        width: "calc(100% - 32px)",
        maxWidth: "440px",
        height: "auto",
        maxHeight: "calc(100% - 32px)",
        margin: "16px",
        overflow: "hidden",
      },
      ".MuiDialogContent-root": {
        padding: "16px 20px",
      },
      ".MuiDialogActions-root": {
        padding: "16px 20px",
      },
    },
  },
}));

// Dialog Title
export const DialogTitleContainer = styling(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  minHeight: "64px",
  fontWeight: 700,
}));

export const DialogTitleStyled = styling(DialogTitle)(({ theme }) => ({
  "&&": {
    padding: 0,
    textAlign: "center",
    fontFamily: "Inter",
    fontWeight: 700,
    color: "#222222",
    fontSize: "24px",
    lineHeight: "24px",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },

  "&& .MuiTypography-root": {
    fontFamily: "Inter",
    fontWeight: 700,
  },

  [theme.breakpoints.down("sm")]: {
    "&&": {
      fontSize: "20px",
      lineHeight: "28px",
      marginRight: "24px",
    },
  },
}));

// Dialog Content
export const DialogContentStyledWrapper = styling(DialogContent)(
  ({ theme }) => ({
    padding: "32px 40px",
    display: "flex",
    flexDirection: "column",
    overflow: "visible",
    borderBottom: `1px solid ${Color.secondaryGrey}`,

    [theme.breakpoints.down("md")]: {
      padding: "24px 32px",
    },

    [theme.breakpoints.down("sm")]: {
      padding: "24px 20px",
    },
  })
);

export const LanguageDialogTitle = styling(DialogTitle)(() => ({
  "&&": {
    fontWeight: 700,
    fontSize: "20px",
  },

  "@media (max-width: 640px)": {
    "&&": {
      padding: "10px 10px",
      textAlign: "left",
    },
  },
}));

export const LanguageGrid = styling("div", { shouldForwardProp })(
  ({ levelsCount }) => ({
    marginTop: "16px",
    display: "grid",
    gridTemplateColumns: `1fr repeat(${levelsCount}, 1fr) 0.05fr`,
    columnGap: "16px",
    rowGap: "5px",
    alignItems: "center",

    "@media (max-width: 1279px)": {
      gridTemplateColumns: `1.2fr repeat(${levelsCount}, 1fr) 0.05fr`,
      columnGap: "10px",
      rowGap: "4px",
    },
  })
);

export const GridHeaderLanguageCell = styling("div")(() => ({
  fontWeight: 700,
  padding: "8px 0",
  fontSize: "16px",

  "@media (max-width: 1279px)": {
    fontSize: "14px",
  },
}));

export const GridHeaderLevelCell = styling("div")(() => ({
  textAlign: "center",
  fontWeight: 700,
  padding: "8px 0",
  whiteSpace: "nowrap",
  fontSize: "16px",

  "@media (max-width: 1279px)": {
    fontSize: "14px",
  },
}));

export const GridHeaderSpacerCell = styling("div")(() => ({
  padding: "8px 0",
}));

export const GridRowLanguageCell = styling("div")(() => ({
  padding: "8px 0",
  fontWeight: 400,
  fontSize: "16px",

  "@media (max-width: 1279px)": {
    fontSize: "14px",
  },
}));

export const GridRowCellCentered = styling("div")(() => ({
  textAlign: "center",
  padding: "8px 0",
}));

export const ProficiencyRadio = styling(Radio, {
  shouldForwardProp: (prop) => prop !== "highlightError",
})(({ highlightError }) => ({
  "&& .MuiSvgIcon-root": {
    fontSize: 20,
  },

  "@media (max-width: 1279px)": {
    "&& .MuiSvgIcon-root": {
      fontSize: 18,
    },
  },
  ...(highlightError
    ? {
        color: "#E53935",
        "&.Mui-checked": {
          color: "#E53935",
        },
      }
    : {
        color: Color.secondaryGrey,
        "&.Mui-checked": {
          color: Color.hiredlyPurple,
        },
      }),
}));

export const AddLanguageSection = styling("div")(() => ({
  marginTop: "16px",
  width: "100%",
  display: "block",

  "@media (max-width: 640px)": {
    textAlign: "left",
  },
}));

export const AddLanguageFormControl = styling(FormControl)(() => ({
  maxWidth: "30%",
  width: "100%",

  "@media (max-width: 1279px)": {
    maxWidth: "240px",
  },

  "@media (max-width: 640px)": {
    maxWidth: "100%",
  },
}));

export const AddLanguageSelect = styling(Select)(() => ({
  "&& .MuiOutlinedInput-notchedOutline": { borderRadius: "12px" },

  width: "100%",              
  display: "block", 
  "@media (max-width: 640px)": {           

    "& .MuiInputBase-root": {
      width: "100%",         
    },

    "& .MuiOutlinedInput-root": {
      width: "100%",          
    },
  },
}));

export const ProficiencyErrorText = styling(Typography)(() => ({
  color: "#F75443",
  fontSize: "14px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  paddingRight: "8px",

  "@media (max-width: 1279px)": {
    whiteSpace: "normal",
  },

  "@media (max-width: 640px)": {
    whiteSpace: "normal",
    paddingRight: 0,
    textAlign: "center",
  },
}));

export const getAddLanguageMenuProps = (isMobile, menuGapHeight, menuWidth) => ({
  anchorOrigin: {
    vertical: isMobile ? "top" : "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: isMobile ? "bottom" : "top",
    horizontal: "left",
  },
  PaperProps: {
    sx: {
      ...(isMobile
        ? { height: menuGapHeight, maxHeight: menuGapHeight }
        : { maxHeight: 320 }),
      ...(menuWidth ? { width: `${menuWidth}px`, minWidth: `${menuWidth}px` } : {}),
      overflowY: "auto",
      borderRadius: 2,
      transform: (isMobile ? "translate3d(0, -15px, 0)" : "translate3d(0, 8px, 0)") + " !important",
    },
  },

});

export const LanguageDialogActions = styling(DialogActions)(() => ({
  justifyContent: "space-between",
  padding: "8px 16px",
  minHeight: "76px",

  "@media (max-width: 640px)": {
    flexDirection: "column-reverse",
    alignItems: "stretch",
    padding: "16px 20px",
    minHeight: "auto",
  },
}));

export const DontShowAgainButton = styling(Button)(() => ({
  fontSize: "14px",
  fontWeight: 500,
  textDecoration: "underline",
  opacity: 0.6,
  textTransform: "none",
  color: "black",

  "@media (max-width: 640px)": {
    alignSelf: "center",
  },
}));

export const ActionsRight = styling("div")(() => ({
  display: "flex",
  gap: "8px",
  alignItems: "center",

  "@media (max-width: 640px)": {
    width: "100%",
    flexDirection: "column",
    alignItems: "stretch",
    gap: "10px",
  },
}));

export const CloseDialogIconButton = styling(IconButton)(() => ({
  position: "absolute",
  right: 16,
  top: 16,
  color: "#222222",
  zIndex: 1,

  "@media (max-width: 640px)": {
    right: 8,
    top: 8,
  },
}));

export const SubtitleText = styling(Typography)(({ theme }) => ({
  fontFamily: "Inter",
  fontSize: "16px",
  lineHeight: "24px",
  color: "#222222",
  fontWeight: 400,
  margin: 0,
  textAlign: "center",

  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    lineHeight: "20px",
  },
}));

export const DoneButton = styling(Button)(({ theme }) => ({
  padding: "12px 48px",
  borderRadius: "100px",
  border: "none",
  backgroundColor: "#353535",
  color: "white",
  fontFamily: "Inter",
  fontSize: "16px",
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.2s ease-in-out",
  minWidth: "200px",
  height: "42px",

  "&:hover": {
    backgroundColor: Color.buttonGrey,
  },

  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    padding: "10px 40px",
    height: "38px",
  },

  "@media (max-width: 640px)": {
    minWidth: "0",
    width: "100%",
  },
}));

export const CloseButton = styling(Button)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  minWidth: "auto",
}));

export const CancelButton = styling(Button)(({ theme }) => ({
  position: "absolute",
  marginRight: "auto",
  fontSize: "16px",

  [theme.breakpoints.down("sm")]: {
    marginRight: "auto",
  },
}));

export const DeleteOutlineIcon = styling("img")(({ theme }) => ({
  width: "16.5px",
  height: "19.5px",
}));

export const MobileLanguageList = styling("div")(() => ({
  marginTop: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
}));

export const MobileLanguageCard = styling("div")(() => ({
  border: `1px solid ${Color.lightGrey}`,
  borderRadius: "12px",
  padding: "12px",
}));

export const MobileLanguageCardHeader = styling("div")(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
}));

export const MobileRadioGroup = styling(RadioGroup)(() => ({
  marginTop: "8px",
}));

export const MobileRadioOption = styling(FormControlLabel)(({ theme }) => ({
  margin: 0,
  padding: "2px 0",
  alignItems: "center",
  ".MuiFormControlLabel-label": {
    fontSize: "16px",
    color: "#222222",
  },

  [theme.breakpoints.down("sm")]: {
    ".MuiFormControlLabel-label": {
      fontSize: "15px",
    },
  },
}));

export const AddLanguageContainer = styling("div")(() => ({
  width: "100%",
  position: "relative",
}));